import { assert, Test } from "oc-e2e-testrunner";
import { Raycaster, Vector3 } from "three";
import { resetArrangement } from "./utils";

export default {
  name: "TestAnschlussUmdrehen",
};

// Reale Konfiguration mit einem LONGLIFE-RIVA-Anschlusselement (Aufbautyp
// "Kein_Zuschnitt_Holzzaun"). Beim Umschalten von "Anschlusselemente umdrehen"
// (anschlussAufRechts) wurden zwar die Pfostenhöhen vertauscht, das geschrägte
// Füllungsmodell selbst wurde aber nicht (bzw. in die falsche Richtung) gespiegelt –
// dadurch stand der hohe Pfosten an der niedrigen Seite des Elementes.
const configuration = {
  inspiration: "Inspiration Longlife Riva Polareiche",
  zaunart: "Sichtschutz",
  serie: "LONGLIFE",
  grundriss: {
    ecken: [
      { id: 3, punkt: { x: -4.675, y: 0 } },
      { id: 4, punkt: { x: 4.675, y: 0 } },
      { id: 535, punkt: { x: -4.234999999999999, y: 2 } },
      { id: 536, punkt: { x: 1.9849999999999999, y: 2 } },
      { id: 882, punkt: { x: 3.7, y: 2 } },
      { id: 883, punkt: { x: 5.1000000000000005, y: 2 } },
    ],
    kanten: [{ ecken: [3, 4] }, { ecken: [535, 536] }, { ecken: [882, 883] }],
  },
  segmente: [
    {
      breite: 9.35,
      partsAndPfosten: [
        {
          type: "Pfosten",
          x0: 8.32,
          pfostenfarbeAuswahlwert: "Polareiche",
          bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
          elementhalterAuswahlwert: "Elementhalter Edelstahl",
          pfostenartAuswahlwert: "Kunststoff Pfosten Longlife",
          verbundankerAuswahlwert: "Verbundanker-Set_mit",
          ummantelungAuswahlwert: "Ummantelung_ohne",
          pfostenkappeAuswahlwert: "Longlife Pfostenkappe Pyramide Edelstahl",
          ankerInnen: false,
          ausgleichGefälle: false,
        },
        {
          type: "Element",
          breite: 0.9,
          höhe: 1.8,
          snap: 1,
          isBreiteKonfigurierbar: false,
          x0: 8.405,
          füllungAuswahlwert: "LONGLIFE RIVA Polareiche",
          form: "Anschluss",
          massAuswahlwert: "Longlife_Riva_90x180_Anschluss",
          anschlussAufRechts: true,
          detailSerie: "LONGLIFE RIVA",
          aufsatzleisteAuswahlwert: "Aufsatzleiste Alu 1861",
          anMauer: "AnMauer_nein",
          isMasselement: false,
          isBestellseite: false,
          elementID: 0,
        },
      ],
      verteilungsstrategieAuswahlwert: "Individuell",
    },
    { breite: 6.22, partsAndPfosten: [], verteilungsstrategieAuswahlwert: "Individuell" },
    { breite: 1.4, partsAndPfosten: [], verteilungsstrategieAuswahlwert: "Individuell" },
  ],
  ecken: [
    { position: { x: -4.675, y: 0, z: 0 } },
    {
      position: { x: 4.674999999999998, y: 0, z: 0 },
      pfosten: {
        type: "Pfosten",
        position: { x: 4.674999999999998, y: 0, z: 0 },
        pfostenfarbeAuswahlwert: "Polareiche",
        bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
        elementhalterAuswahlwert: "Elementhalter Edelstahl",
        pfostenartAuswahlwert: "Kunststoff Pfosten Longlife",
        verbundankerAuswahlwert: "Verbundanker-Set_mit",
        ummantelungAuswahlwert: "Ummantelung_ohne",
        pfostenkappeAuswahlwert: "Longlife Pfostenkappe Pyramide Edelstahl",
        ankerInnen: false,
        ausgleichGefälle: false,
      },
    },
    { position: { x: -4.234999999999999, y: 0, z: 2 } },
    { position: { x: 1.9849999999999994, y: 0, z: 2 } },
    { position: { x: 3.7, y: 0, z: 2 } },
    { position: { x: 5.1000000000000005, y: 0, z: 2 } },
  ],
  aufbauten: [],
  pfostenartAuswahlwert: "Kunststoff Pfosten Longlife",
  pfostenfarbeAuswahlwert: "Polareiche",
  bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
  elementhalterAuswahlwert: "Elementhalter Edelstahl",
  verteilungsstrategieAuswahlwert: "StandardLinks",
  pageName: "Zauneditor",
};

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

const höheVon = (pfosten: any): number => pfosten.gesuchtePfostenHöhe;

// Misst per Raycast von oben nach unten, wie hoch die Oberkante des Elementes an einem
// Punkt ist. Unsichtbare Hilfskörper (raycastBox) und der Boden werden ignoriert.
// Liefert null, wenn nichts getroffen wird.
function oberkanteBei(weltPunkt: Vector3): number | null {
  const ray = new Raycaster(
    new Vector3(weltPunkt.x, weltPunkt.y + 6, weltPunkt.z),
    new Vector3(0, -1, 0),
    0,
    12
  );
  const treffer = ray
    .intersectObject(APP.scene, true)
    .filter(
      (t: any) =>
        t.object.name !== "raycastBox" &&
        t.object.material?.name !== "invisible" &&
        t.object.visible !== false &&
        t.point.y > 0.05
    );
  return treffer.length ? treffer[0].point.y : null;
}

export const testAnschlussUmdrehenSpiegeltModell: Test = async () => {
  resetArrangement(true);
  const agt = APP.arrangement;
  agt.fromJSON(configuration);
  agt.update3d();
  agt.update3d();

  const element = agt.getElemente().find((e: any) => e.isAnschluss);
  assert.isTrue(element != null, "Die Konfiguration muss ein Anschlusselement enthalten");

  const füllung = element.flügel[0].füllung;
  const breite: number = füllung.size.x;
  // Weltpunkt an einem Bruchteil der Elementbreite (0 = eine Kante, 1 = andere Kante).
  const weltAnBruchteil = (t: number): Vector3 =>
    füllung.localToWorld(new Vector3(breite * t, 0, 0));

  // Die beiden Nachbarpfosten den beiden Elementkanten zuordnen (Seite A = lokales x≈0,
  // Seite B = lokales x≈breite), unabhängig von links/rechts und Grundrissdrehung.
  const kanteA = weltAnBruchteil(0);
  const nachbarn = element.getNachbarn();
  const pfostenA = nachbarn.reduce((best: any, p: any) =>
    p.getWorldPosition().distanceTo(kanteA) < best.getWorldPosition().distanceTo(kanteA) ? p : best
  );
  const pfostenB = nachbarn.find((p: any) => p !== pfostenA);

  // Prüft für einen Umdrehen-Zustand, dass der höhere Pfosten an der hohen Seite des
  // Modells steht. Misst die Modellschräge real per Raycast.
  // Wichtig: update3d() + APP.dirty() und dann kurz warten, damit die Änderung bis in
  // die gerenderte Geometrie durchschlägt (APP.dirty ist asynchron).
  const seiteAIstHoch = async (anschlussAufRechts: boolean): Promise<boolean> => {
    element.anschlussAufRechts = anschlussAufRechts;
    agt.update3d();
    agt.update3d();
    APP.dirty();
    await sleep(3000);
    APP.scene.updateMatrixWorld(true);

    const oberkanteA = oberkanteBei(weltAnBruchteil(0.25));
    const oberkanteB = oberkanteBei(weltAnBruchteil(0.75));
    assert.isTrue(
      oberkanteA != null && oberkanteB != null,
      "Die Modelloberkante des Anschlusselementes muss per Raycast messbar sein"
    );

    const modellSeiteAHoch = (oberkanteA as number) > (oberkanteB as number);
    const pfostenSeiteAHoch = höheVon(pfostenA) > höheVon(pfostenB);

    // Kernforderung: An der Seite, an der das Modell hoch ist, muss auch der höhere
    // Pfosten stehen.
    assert.equals(
      modellSeiteAHoch,
      pfostenSeiteAHoch,
      `Der höhere Pfosten muss an der hohen Seite des Modells stehen ` +
        `(anschlussAufRechts=${anschlussAufRechts}, ` +
        `Modelloberkante A=${(oberkanteA as number).toFixed(3)} / B=${(
          oberkanteB as number
        ).toFixed(3)}, Pfostenhöhe A=${höheVon(pfostenA)} / B=${höheVon(pfostenB)})`
    );

    return modellSeiteAHoch;
  };

  const seiteAHochBeiFalse = await seiteAIstHoch(false);
  const seiteAHochBeiTrue = await seiteAIstHoch(true);

  // Zusätzlich: Das Umschalten muss das Modell tatsächlich umdrehen – die hohe Seite
  // muss vorher/nachher wechseln (vor dem Fix blieb sie trotz vertauschter Pfostenhöhen
  // gleich bzw. drehte in die falsche Richtung).
  assert.notEquals(
    seiteAHochBeiFalse,
    seiteAHochBeiTrue,
    "Das Umdrehen muss die hohe Seite des Anschlussmodells tatsächlich wechseln"
  );
};
